// Brain Overview — "Cipher's Mind" (faithful port of the approved artifact).
// 3 decision cards + central capability graph + right rail
// (Company Health / Momentum / System Pulse / Recent Observations) + the always-
// available conversation with proactive messaging & the Executive Timeline.
//
// Real data via selectors (real-or-null); every gap that has no real data source
// renders an honest empty/disabled state (BrainStates.jsx) instead of fabricating
// a replacement. The graph/conversation behavior lives in viz/CipherGraph +
// ExecutiveConversation.
import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBrainResource } from "../lib/hooks";
import { selectDecisionCards, selectMomentumCounts } from "../lib/selectors";
import ExecutiveConversation from "../ExecutiveConversation";
import { EmptyState, LoadingState, ErrorState } from "../BrainStates";

function d(r) { return r && r.status === "ok" ? r.data : null; }
function list(r, key) { const x = d(r); return x ? x[key] || (Array.isArray(x) ? x : []) : []; }
function num(v) { return typeof v === "number" && !Number.isNaN(v) ? v : null; }

export default function BrainOverview({ user, brainStage = null }) {
  const navigate = useNavigate();
  const graphRef = useRef(null);

  // real resources (each degrades to disabled/empty/error without fabricating)
  const queue = useBrainResource("/executive/queue", { params: { limit: 25 } });
  const predictionsSummary = useBrainResource("/predictions/summary");
  const recommendationsSummary = useBrainResource("/recommendations/summary");
  const observations = useBrainResource("/intelligence/observations");
  const experiments = useBrainResource("/intelligence/experiments");
  const journalSummary = useBrainResource("/journal/summary");
  const lessonsSummary = useBrainResource("/lessons/summary");
  // Company Health (right rail) — real business-unit/integration/scheduler
  // topology, composed server-side from /bu/health, /schedulers/health,
  // /integrations/health and /circuit-breaker/status.
  const healthTopology = useBrainResource("/organization/health-topology");
  // System Pulse (right rail) — real scheduler + agent + integration status.
  const schedulersHealth = useBrainResource("/schedulers/health");
  const agentsStatus = useBrainResource("/agents/status");
  const integrationsHealth = useBrainResource("/integrations/health");

  const nowClock = useMemo(() => {
    const dt = new Date();
    return { time: dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }), date: dt.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) };
  }, []);

  // decision cards — real (queue) only. When the queue has no items, there are no
  // decision cards to show; render EmptyState rather than inventing placeholders.
  const { cards, cardsArePlaceholder } = useMemo(() => {
    const real = selectDecisionCards(d(queue));
    if (real.length) {
      return {
        cardsArePlaceholder: false,
        cards: real.map((c, i) => ({
          item_id: c.item_id, rank: c.rank || i + 1, title: c.title, accent: ["g", "o", "p"][i % 3],
          weeklyImpact: c.dollarsPerDay != null ? Math.round(c.dollarsPerDay * 7) : null,
          impactNote: c.dollarsPerDay != null ? null : "Impacting performance",
        })),
      };
    }
    return { cardsArePlaceholder: true, cards: [] };
  }, [queue.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // graph node status — real counts where they exist; else absent (no mock activity)
  const realStatus = useMemo(() => {
    const obs = list(observations, "observations").length || num(d(observations) && d(observations).total);
    const exp = list(experiments, "experiments").length || num(d(experiments) && d(experiments).total);
    const out = {};
    const put = (id, n, suffix) => { if (num(n) != null) out[id] = n.toLocaleString() + " " + suffix; };
    put("predictions", num(d(predictionsSummary) && d(predictionsSummary).total_predictions), "active");
    put("recommendations", num(d(recommendationsSummary) && d(recommendationsSummary).total), "pending");
    put("observations", num(obs), "new");
    put("experiments", num(exp), "running");
    put("knowledge", num(d(lessonsSummary) && d(lessonsSummary).total), "insights");
    put("memory", num(d(journalSummary) && d(journalSummary).total), "logged");
    return out;
  }, [predictionsSummary.data, recommendationsSummary.data, observations.data, experiments.data, lessonsSummary.data, journalSummary.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // momentum — real counts only; null stays null (rendered as "—", never a fabricated number)
  const mom = useMemo(() => selectMomentumCounts({ journalSummary: d(journalSummary), experiments: list(experiments, "experiments"), lessonsSummary: d(lessonsSummary) }), [journalSummary.data, experiments.data, lessonsSummary.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const realObs = list(observations, "observations").slice(0, 3).map((o) => ({ t: o.title || o.summary || o.text || "New observation", ago: "" }));

  // Company Health — real business-unit nodes from GET /api/organization/health-topology.
  const healthNodes = list(healthTopology, "nodes");
  const healthBUs = healthNodes.filter((n) => n && n.type === "business_unit");
  const STATUS_COLOR = { green: "var(--green)", yellow: "var(--amber)", red: "#e5484d", unknown: "var(--dim)" };
  const STATUS_LABEL = { green: "Healthy", yellow: "Degraded", red: "Down", unknown: "Unknown" };

  // System Pulse — real counts from /schedulers/health, /agents/status, /integrations/health.
  const schedData = d(schedulersHealth);
  const agentsData = d(agentsStatus);
  const integrationsList = list(integrationsHealth, "integrations");
  const integrationCounts = integrationsList.reduce(
    (acc, i) => { const s = (i && i.status) || "unknown"; acc[s] = (acc[s] || 0) + 1; return acc; },
    {}
  );

  const openDecision = (card) => card.item_id && navigate(`/brain/decisions/${encodeURIComponent(card.item_id)}`);
  const fmt$ = (n) => "$" + Math.round(n).toLocaleString();
  const momCells = [
    { v: mom.decisions ?? "—", k: "Decisions Made" },
    { v: mom.experiments ?? "—", k: "Experiments Completed" },
    { v: mom.knowledge ?? "—", k: "New Learnings" },
  ];

  return (
    <>
      <header className="topbar">
        <div className="greeting">
          <p>I've been thinking overnight. Here are {cards.length} decisions that deserve your attention.</p></div>
        <div className="status">
          <div><div className="time num">{nowClock.time}</div><div className="date num">{nowClock.date}</div></div>
        </div>
      </header>

      {/* decision cards */}
      <section className="decks">
        {cardsArePlaceholder ? (
          <EmptyState
            title="No decisions in the queue"
            detail="Cipher has nothing pending review right now."
            testId="decision-queue-empty"
          />
        ) : (
          <>
            {cards.slice(0, 3).map((c, i) => (
              <article key={c.item_id || i} className={`deck ${c.accent} in d${i + 1}`} tabIndex={0} onClick={() => openDecision(c)}>
                <div className="rank">{String(c.rank).padStart(2, "0")}</div>
                <h3>{c.title}</h3>
                <div className="impact">
                  {c.weeklyImpact != null ? <>+{fmt$(c.weeklyImpact)} <span className="sub">/ week</span></> : <span className="sub">{c.impactNote || "—"}</span>}
                </div>
                <span className="cta">Review Decision <span className="arrow">→</span></span>
              </article>
            ))}
            <div className="carousel"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg></div>
          </>
        )}
      </section>

      {/* graph + rail */}
      <div className="grid">
        {/* THE BRAIN. Replaces the capability graph that stood here: that graph
            labelled itself "Node status & activity illustrative — binds to
            live telemetry on integration", i.e. it was drawing activity it did
            not have. The Brain stage renders only real organisation state, so
            the surface stops showing motion it cannot account for. */}
        {brainStage}

        <aside className="rail">
          <section className="card health in d3">
            <div className="head"><div className="title">Company Health</div></div>
            {healthTopology.status === "loading" ? (
              <LoadingState label="Loading company health" rows={2} testId="health-loading" />
            ) : healthTopology.status === "disabled" ? (
              <EmptyState title="Company Health not enabled" detail="Founder access is required for this topology." testId="health-disabled" />
            ) : healthTopology.status === "error" ? (
              <ErrorState detail={healthTopology.error && healthTopology.error.message} onRetry={healthTopology.reload} testId="health-error" />
            ) : healthBUs.length ? (
              <div className="stat" data-testid="health-bu-list">
                {healthBUs.map((b) => (
                  <div className="row" key={b.id}>
                    <span className="dot" style={{ background: STATUS_COLOR[b.status] || STATUS_COLOR.unknown, boxShadow: `0 0 9px ${STATUS_COLOR[b.status] || STATUS_COLOR.unknown}` }} />
                    <span>{b.label}</span>
                    <span className="ago num" style={{ marginLeft: "auto" }}>{STATUS_LABEL[b.status] || b.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="No business units reported" testId="health-empty" />
            )}
          </section>

          <section className="card mom in d3">
            <div className="head"><div className="title">Momentum</div></div>
            <div className="mom-grid">{momCells.map((m) => (<div key={m.k}><div className="v num">{m.v}</div><div className="k">{m.k}</div></div>))}</div>
            <span className="link">View momentum report <span className="arrow">→</span></span>
          </section>

          <section className="card in d4">
            <div className="head"><div className="title">System Pulse</div></div>
            {schedulersHealth.status === "loading" || agentsStatus.status === "loading" || integrationsHealth.status === "loading" ? (
              <LoadingState label="Loading system pulse" rows={3} testId="pulse-loading" />
            ) : schedulersHealth.status === "disabled" && agentsStatus.status === "disabled" && integrationsHealth.status === "disabled" ? (
              <EmptyState title="System Pulse not enabled" detail="Founder access is required for scheduler/agent/integration telemetry." testId="pulse-disabled" />
            ) : schedulersHealth.status === "error" && agentsStatus.status === "error" && integrationsHealth.status === "error" ? (
              <ErrorState detail={schedulersHealth.error && schedulersHealth.error.message} onRetry={() => { schedulersHealth.reload(); agentsStatus.reload(); integrationsHealth.reload(); }} testId="pulse-error" />
            ) : (
              <div className="stat" data-testid="pulse-stats">
                <div className="row">
                  <span className="dot" style={agentsData && agentsData.scheduler_running === false ? { background: "#e5484d", boxShadow: "0 0 9px #e5484d" } : undefined} />
                  <span>Scheduler</span>
                  <span className="ago num" style={{ marginLeft: "auto" }}>{agentsData ? (agentsData.scheduler_running ? "Running" : "Stopped") : "—"}</span>
                </div>
                <div className="row">
                  {/* needs_attention_jobs, not unhealthy_jobs (#262): since the health count
                      became strict, "unhealthy" includes jobs that merely have no
                      recorded run, and an amber dot that is always on is one nobody
                      reads. The count beside it stays strict. */}
                  <span className="dot" style={schedData && schedData.needs_attention_jobs > 0 ? { background: "var(--amber)", boxShadow: "0 0 9px var(--amber)" } : undefined} />
                  <span>Scheduled jobs</span>
                  <span className="ago num" style={{ marginLeft: "auto" }}>{schedData ? `${schedData.healthy_jobs}/${schedData.total_jobs} healthy` : "—"}</span>
                </div>
                <div className="row">
                  <span className="dot" style={(integrationCounts.red || 0) > 0 ? { background: "#e5484d", boxShadow: "0 0 9px #e5484d" } : (integrationCounts.yellow || 0) > 0 ? { background: "var(--amber)", boxShadow: "0 0 9px var(--amber)" } : undefined} />
                  <span>Integrations</span>
                  <span className="ago num" style={{ marginLeft: "auto" }}>{integrationsList.length ? `${integrationCounts.green || 0}/${integrationsList.length} up` : "—"}</span>
                </div>
              </div>
            )}
          </section>

          <section className="card obs in d4">
            <div className="head" style={{ marginBottom: "6px" }}><div className="title">Recent Observations</div>
              {realObs.length ? <span className="pill">{realStatus.observations || `${realObs.length} shown`}</span> : null}</div>
            {realObs.length ? (
              realObs.map((o, i) => (
                <div className="row" key={i}><svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="3" /><path d="M2 12s3.5-6 10-6 10 6 10 6" /></svg>
                  <div className="t">{o.t}</div>{o.ago ? <span className="ago num">{o.ago}</span> : null}</div>
              ))
            ) : (
              <EmptyState title="No recent observations" testId="observations-empty" />
            )}
            <span className="link" style={{ marginTop: "12px" }}>View all observations <span className="arrow">→</span></span>
          </section>
        </aside>
      </div>

      <ExecutiveConversation graph={graphRef} />
    </>
  );
}
